import { create } from 'zustand';
import { useWindowStore } from '@/wm/store';
import { initialIndex, step, switchList } from './switcher';

/**
 * The task switcher's own state.
 *
 * Everything the box draws is captured when it opens and never recomputed
 * while it is up. That is the whole trick: the order comes from the z-order,
 * and committing a switch changes the z-order, so a live list would reshuffle
 * itself under the highlight the moment anything happened. Windows froze the
 * list for the same reason, which is why holding Alt and pressing Tab four
 * times walks four windows back rather than flapping between two.
 *
 * Freezing the captions and icons along with the ids has a second benefit: the
 * overlay never subscribes to the window store, so it does not re-render on
 * every frame of a drag it is not even on screen for.
 */

export interface SwitchItem {
  id: string;
  title: string;
  icon: string;
}

interface AltTabStore {
  /** In switch order, frozen at the moment the box opened. Empty when shut. */
  items: SwitchItem[];
  index: number;
  open: boolean;

  /** Alt+Tab pressed with the box shut. Does nothing if there is nothing to show. */
  begin(backwards: boolean): void;
  /** Alt+Tab pressed again with the box up. */
  cycle(backwards: boolean): void;
  /** The pointer moved over an icon. */
  select(index: number): void;
  /** Alt released, or an icon clicked: switch to the highlighted window. */
  commit(): void;
  /** Escape, or the page losing focus: leave everything as it was. */
  cancel(): void;
}

const SHUT = { items: [], index: 0, open: false };

export const useAltTabStore = create<AltTabStore>((set, get) => ({
  items: [],
  index: 0,
  open: false,

  begin(backwards) {
    const list = switchList(useWindowStore.getState().windows);
    // Nothing is running. XP drew an empty box here; there is more dignity in
    // drawing nothing at all.
    if (list.length === 0) return;

    set({
      items: list.map((w) => ({ id: w.id, title: w.title, icon: w.icon })),
      index: initialIndex(list.length, backwards),
      open: true,
    });
  },

  cycle(backwards) {
    const { items, index, open } = get();
    if (!open) return;
    set({ index: step(index, items.length, backwards) });
  },

  select(index) {
    const { items, open } = get();
    if (!open || index < 0 || index >= items.length) return;
    set({ index });
  },

  commit() {
    const { items, index, open } = get();
    if (!open) return;
    set(SHUT);

    const id = items[index]?.id;
    // Gone while the box was up — closed from a menu, or taken down with its
    // owner. Switching to nothing is not an error, it is just nothing.
    if (!id) return;
    // `focus` restores a minimized window on the way, which is what lets the
    // switcher reach one at all.
    useWindowStore.getState().focus(id);
  },

  cancel() {
    if (!get().open) return;
    set(SHUT);
  },
}));
